import dayjs from 'dayjs';
import { isCdkProxyConfigured, nuaProxyUrl } from '../lib/appConfig';
import { cdkProxyPath } from '../lib/cdkProxy';
import { getIdToken } from '../lib/cognitoAuth';
import { proxyExternalMutation } from './externalMutationProxy';

// POST /templates responde sus errores con `error` (y `detail` en el límite de
// plantillas), no con `message` como el resto de la API. Algunos vienen en inglés
// porque el endpoint nació para el widget de HubSpot: se traducen para el asesor.
const TEMPLATE_SEND_ERRORS = {
  'Failed to send template to Meta': 'WhatsApp rechazó el envío de la plantilla. Revisa que el cliente pueda recibirla e intenta de nuevo.',
  'Failed to process templates': 'No se pudo procesar el envío de la plantilla. Intenta de nuevo.',
};

export const templateSendErrorMessage = (body, status) => {
  const base = TEMPLATE_SEND_ERRORS[body?.error] || body?.error || body?.message;
  if (!base) return `Error al enviar la plantilla (${status})`;
  // `detail` del límite llega como "Limit exceeded: 500/500 ...": solo se
  // conservan los números, el resto es texto técnico en inglés.
  const usage = typeof body?.detail === 'string' ? body.detail.match(/(\d+)\s*\/\s*(\d+)/) : null;
  return usage ? `${base} (${usage[1]} de ${usage[2]} usadas)` : base;
};

/**
 * PUT del archivo con progreso real. `fetch` no reporta el avance de una subida,
 * así que con progreso se usa XMLHttpRequest (upload.onprogress).
 */
const putWithProgress = (uploadUrl, body, contentType, onProgress) =>
  new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', uploadUrl);
    xhr.setRequestHeader('Content-Type', contentType);
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable && event.total > 0) {
        onProgress(Math.min(100, Math.round((event.loaded / event.total) * 100)));
      }
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        onProgress(100);
        resolve();
      } else {
        reject(new Error(`Error al subir el adjunto (${xhr.status}): ${xhr.responseText || ''}`));
      }
    };
    xhr.onerror = () => reject(new Error('Error de red al subir el adjunto'));
    xhr.onabort = () => reject(new Error('La subida del adjunto se canceló'));
    xhr.send(body);
  });

// El endpoint de subida de adjuntos responde en inglés y, si el tipo no se acepta,
// con la lista completa de tipos permitidos: demasiado largo y técnico para el
// asesor. Aquí se traduce a un mensaje corto que dice qué sí se puede enviar.
const ATTACHMENT_KIND_LABELS = { IMAGE: 'imágenes', VIDEO: 'videos', AUDIO: 'audios', DOCUMENT: 'documentos' };

const fileExtension = (name) => {
  const dot = (name || '').lastIndexOf('.');
  return dot > 0 ? name.slice(dot + 1).toUpperCase() : '';
};

const formatMegabytes = (bytes) =>
  `${(bytes / (1024 * 1024)).toLocaleString('es-CO', { maximumFractionDigits: 1 })} MB`;

export const attachmentUploadErrorMessage = (body, status, file) => {
  const raw = typeof body?.message === 'string' ? body.message : '';

  if (raw.startsWith('Unsupported contentType')) {
    const type = file?.type || '';
    const ext = fileExtension(file?.name);
    if (type.startsWith('audio/')) {
      return `WhatsApp no acepta audios ${ext || 'en ese formato'}. Usa MP3, M4A, AAC, AMR u OGG.`;
    }
    if (type.startsWith('video/')) return `WhatsApp no acepta videos ${ext || 'en ese formato'}. Usa MP4 o 3GP.`;
    if (type.startsWith('image/')) return `WhatsApp no acepta imágenes ${ext || 'en ese formato'}. Usa JPG o PNG.`;
    return `WhatsApp no permite enviar archivos ${ext || 'de ese tipo'}. Se aceptan PDF, Word, Excel, PowerPoint y texto.`;
  }
  if (status === 404) return 'La conversación ya no está activa. Actualiza la bandeja.';
  if (status >= 500) return 'No se pudo preparar la subida del archivo. Intenta de nuevo.';
  return raw || `Error al preparar la subida (${status})`;
};

export const attachmentTooLargeMessage = (file, kind, maxBytes) =>
  `El archivo pesa ${formatMegabytes(file.size)} y WhatsApp acepta hasta ${formatMegabytes(maxBytes)} en ${
    ATTACHMENT_KIND_LABELS[kind] || 'archivos'
  }.`;

class NuaTalkerNthwService {
  // Solo endpoints de lectura conocidos; conserva el base path configurado del entorno.
  async getReportResource(resource, params, signal) {
    const resources = {
      sends: '/metrics/v2/templates/sends',
      templates: '/metrics/v2/templates/summary',
      conversations: '/metrics/v2/conversations/series',
    };
    if (!resources[resource]) throw new Error('Recurso de reporte no válido');
    const query = this._buildQuery({ ...params, portalId: this.portalId });
    const response = await this._nthwFetch(`${resources[resource]}?${query}`, { signal });
    if (!response.ok) throw new Error(`No se pudo consultar el reporte (${response.status}).`);
    return response.json();
  }

  async searchConversations(body, signal) {
    const response = await this._nthwFetch(`/conversations/portal/${encodeURIComponent(this.portalId)}/search`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body), signal,
    });
    if (!response.ok) {
      let detail = '';
      try { detail = (await response.json())?.message || ''; } catch (_) { /* ignore */ }
      if (response.status === 403 && detail === 'Missing Authentication Token') {
        throw new Error('La búsqueda aún no está publicada en este entorno de NTHW.');
      }
      throw new Error(detail || `No se pudo buscar conversaciones (${response.status}).`);
    }
    return response.json();
  }

  constructor(portalId) {
    this.endpoint = process.env.REACT_APP_NUA_TALKER_NTHW;
    this.portalId = portalId;
    this.baseUrl = `${process.env.REACT_APP_NUA_TALKER_NTHW}/templates/portal/${portalId}`;
  }

  _buildQuery(params = {}) {
    return Object.entries(params)
      .filter(([, v]) => v !== undefined && v !== null && String(v).length > 0)
      .map(
        ([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`
      )
      .join('&');
  }

  /**
   * Con `REACT_APP_NUA_PROXY_URL` va a `/nthw/{portalId}/...` (CDK).
   * Si no, al micro NTHW. El PUT prefirmado a S3 no pasa por acá.
   */
  async _nthwFetch(path, init = {}) {
    const base = String(this.endpoint || '').replace(/\/$/, '');
    let raw = String(path || '');
    if (
      base &&
      (raw === base || raw.startsWith(`${base}/`) || raw.startsWith(`${base}?`))
    ) {
      raw = raw.slice(base.length);
    }
    const clean = `/${raw.replace(/^\/+/, '')}`;
    const method = init.method || 'GET';
    const headers = { ...(init.headers || {}) };

    if (isCdkProxyConfigured() && this.portalId) {
      const token = await getIdToken();
      if (!token) {
        const error = new Error('No hay sesión activa');
        error.status = 401;
        throw error;
      }
      headers.Authorization = `Bearer ${token}`;
      const url = `${nuaProxyUrl()}${cdkProxyPath('nthw', this.portalId, clean)}`;
      return fetch(url, { ...init, method, headers });
    }

    return fetch(`${base}${clean}`, { ...init, method, headers });
  }

  /**
   * Cerrados y reportería viven en un RestApi aparte (closed-tickets-apigateway-
   * stack-v2) por el límite de 500 recursos de CloudFormation del API principal.
   * Su basePath en el dominio custom es "{env}-closed-tickets", hermano de "{env}"
   * y no anidado bajo él (un basePath anidado no funciona: API Gateway no prioriza
   * el mapeo más largo sobre el corto en el mismo dominio).
   *
   * Va con `fetch` directo y NO con `_nthwFetch`: el proxy CDK solo enruta al API
   * principal (`/nthw/{id}/…`), no a este basePath.
   */
  _closedTicketsFetch(path, init = {}) {
    const base = String(this.endpoint || '').replace(/\/([^/]+)$/, '/$1-closed-tickets');
    return fetch(`${base}${path}`, init);
  }

  async getProperties(id) {
    const response = await this._nthwFetch(`/hubspot/properties/${id}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
    });
    if (!response.ok) {
      const errorText = await response.text();
      console.error('Error al obtener las propiedades', errorText);
      return null;
    }
    const data = await response.json();
    return data.properties.results ?? null;
  }

  async getTemplates() {
    try {
      const response = await this._nthwFetch(this.baseUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
      });
      if (!response.ok) {
        throw new Error('Error al obtener las plantillas');
      }
      return await response.json();
    } catch (error) {
      throw new Error(`Error en la solicitud: ${error.message}`);
    }
  }

  async getTemplate(id, phoneNumber) {
    const url = `${this.baseUrl}/template/${id}`;
    const response = await this._nthwFetch(`${url}?systemNumber=${phoneNumber}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
    });
    return await response.json();
  }

  async createTemplate(data) {
    try {
      const url = `${this.baseUrl}`;

      const response = await this._nthwFetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(data),
      });
      if (!response.ok) {
        throw new Error('Error al crear la plantilla');
      }
      return await response.json();
    } catch (error) {
      throw new Error(`Error en la solicitud: ${error.message}`);
    }
  }

  async updateTemplate(data) {
    const id = data?.body?.id;
    const url = `${this.baseUrl}/template/${id}`;
    try {
      const response = await this._nthwFetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          body: data.body,
          systemNumber: data.systemNumber,
          wabaId: data.wabaId,
        }),
      });
      if (!response.ok) {
        throw new Error('Error al actualizar la plantilla');
      }
      return await response.json();
    } catch (error) {
      throw new Error(`Error en la solicitud: ${error.message}`);
    }
  }

  async deleteTemplate(templateId) {
    try {
      const response = await this._nthwFetch(`${this.baseUrl}/template/${templateId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
      });
      if (!response.ok) {
        throw new Error('Error al eliminar la plantilla');
      }
      return await response.json();
    } catch (error) {
      throw new Error(`Error en la solicitud: ${error.message}`);
    }
  }

  async updateChannel(hubSpotId, systemNumber, hubspotChannelAccountId) {
    try {
      const response = await this._nthwFetch(
        `/channels/${hubSpotId}/systemNumber/${systemNumber}`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({
            hubspotChannelAccountId,
          }),
        }
      );
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message || 'Error al actualizar la configuración del canal'
        );
      }
      return await response.json();
    } catch (error) {
      throw new Error(`Error en la solicitud: ${error.message}`);
    }
  }

  async updatePhoneSettings(hubSpotId, systemNumber, settings) {
    try {
      const response = await this._nthwFetch(
        `/channels/${hubSpotId}/systemNumber/${systemNumber}`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify(settings),
        }
      );
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message || 'Error al actualizar la configuración'
        );
      }
      return await response.json();
    } catch (error) {
      throw new Error(`Error en la solicitud: ${error.message}`);
    }
  }

  async getActiveChats(hubSpotId, { limit = 5000, nextToken, viewerId } = {}) {
    const qs = this._buildQuery({
      limit,
      nextToken,
      // E18: solo tiene efecto en los canales con `privateAssignedConversations`
      // activo — el backend ignora este parámetro en los demás.
      viewerId,
    });
    const url = `${this.endpoint}/conversations/portal/${hubSpotId}${
      qs ? `?${qs}` : ''
    }`;
    const response = await this._nthwFetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
    });
    return await response.json();
  }

  async deleteConversation({
    conversationId,
    hubSpotId,
    systemNumber,
    wa_id,
  }) {
    const payload = {
      portalId: hubSpotId,
      phoneId: systemNumber,
      wa_id,
    };
    try {
      return await proxyExternalMutation({
        service: 'nthw',
        path: `/conversations/${conversationId}`,
        method: 'DELETE',
        body: payload,
        queryParams: hubSpotId ? { portalId: hubSpotId } : undefined,
      });
    } catch (err) {
      if (isCdkProxyConfigured()) throw err;
      // eslint-disable-next-line no-console
      console.warn(
        '[auditoria] proxy nthw no disponible, se elimina directo en Social Media',
        err?.message || err,
      );
    }
    const response = await this._nthwFetch(
      `/conversations/${conversationId}`,
      {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(payload),
      }
    );
    return await response.json();
  }

  /**
   * Transfiere una conversación a un asesor humano, enviando un resumen
   * de lo que venía pasando en el chat para que el asesor arranque en caliente.
   *
   * Contrato NTHW: `POST {endpoint}/conversations/{conversationId}/transfer`
   * con body `{ "summary": "..." }`. Solo headers JSON; **no** se usa API key.
   *
   * @param {object} params
   * @param {string} params.conversationId - ID de la conversación a transferir.
   * @param {string} [params.summary] - Resumen de la conversación para el asesor.
   * @returns {Promise<any>} Respuesta del backend.
   */
  async transferConversation({ conversationId, summary = '' }) {
    if (!conversationId) {
      throw new Error('conversationId es requerido para transferir la conversación');
    }
    const payload = { summary: String(summary || '') };
    try {
      return await proxyExternalMutation({
        service: 'nthw',
        path: `/conversations/${conversationId}/transfer`,
        method: 'POST',
        body: payload,
        queryParams: this.portalId ? { portalId: this.portalId } : undefined,
      });
    } catch (err) {
      if (isCdkProxyConfigured()) throw err;
      // eslint-disable-next-line no-console
      console.warn(
        '[auditoria] proxy nthw no disponible, se transfiere directo en Social Media',
        err?.message || err,
      );
    }
    const response = await this._nthwFetch(
      `/conversations/${conversationId}/transfer`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(payload),
      }
    );
    if (!response.ok) {
      const errorText = await response.text().catch(() => '');
      throw new Error(
        `Error al transferir la conversación (${response.status}): ${errorText || response.statusText}`
      );
    }
    return await response.json().catch(() => ({}));
  }

  // ─────────────────────────────────────────────────────────────────────────
  // BANDEJA NATIVA — E2/E3/E4/E7: candado de asignación, envío como humano,
  // cierre y adjuntos. Contratos en nuatalker-nthw-backend-app.
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Reclama una conversación para el asesor (candado E2). 409 si ya está
   * asignada a otro asesor o si la conversación no existe.
   * @param {string} conversationId
   * @param {string} assignedTo - Email del asesor (identidad Cognito).
   */
  async claimConversation(conversationId, assignedTo) {
    const response = await this._nthwFetch(
      `/conversations/${conversationId}/claim`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ assignedTo }),
      }
    );
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.message || `Error al reclamar la conversación (${response.status})`);
    }
    return await response.json();
  }

  /**
   * Suelta una conversación reclamada por el asesor. 409 si no era suya.
   * @param {string} conversationId
   * @param {string} assignedTo
   */
  async releaseConversation(conversationId, assignedTo) {
    const response = await this._nthwFetch(
      `/conversations/${conversationId}/release`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ assignedTo }),
      }
    );
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.message || `Error al soltar la conversación (${response.status})`);
    }
    return await response.json();
  }

  /**
   * Cierra la conversación (E4). Exige al menos una tipificación (E8): una por
   * categoría, cada una con `categoryId`/`subcategoryId` — sin eso responde 400.
   * @param {string} conversationId
   * @param {string} closedBy
   * @param {Array<{categoryId: string, subcategoryId: string}>} typifications
   */
  async closeConversation(conversationId, closedBy, typifications) {
    const response = await this._nthwFetch(
      `/conversations/${conversationId}/close`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        // El backend espera 'closedBy' (no 'assignedTo') y valida que
        // coincida con el assignedTo actual de la conversación.
        body: JSON.stringify({ closedBy, typifications }),
      }
    );
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.message || `Error al cerrar la conversación (${response.status})`);
    }
    return await response.json();
  }

  /**
   * Catálogo de tipificación del portal (E8/E15): categorías activas con sus
   * subcategorías activas, para el selector de "Cerrar caso".
   * @param {string} portalId
   * @returns {Promise<{data: Array<{categoryId: string, name: string, subcategories: Array<{id: string, name: string}>}>}>}
   */
  async getTypificationCatalog(portalId, includeInactive = false) {
    const response = await this._nthwFetch(
      `/typification/${portalId}${includeInactive ? '?includeInactive=true' : ''}`,
      {
        method: 'GET',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      }
    );
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.message || `Error al cargar el catálogo de tipificación (${response.status})`);
    }
    return await response.json();
  }

  /**
   * Crea una categoría de tipificación (catálogo interno de Nua, no toca
   * HubSpot). `type` por defecto es "select" (usa subcategoryNames); para
   * "string"/"number"/"date"/"boolean" no aplica subcategoryNames — el
   * agente escribe/elige un valor suelto de ese tipo al cerrar.
   * @param {string} portalId
   * @param {{ name: string, type?: 'select'|'string'|'number'|'date'|'boolean', required?: boolean, subcategoryNames?: string[] }} body
   */
  async createTypificationCategory(portalId, body) {
    const response = await this._nthwFetch(`/typification/${portalId}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.message || `Error al crear la categoría (${response.status})`);
    }
    return await response.json();
  }

  /**
   * Actualiza una categoría: nombre, activa/inactiva, o el array completo de
   * subcategorías (agregar/renombrar/quitar/desactivar una subcategoría se
   * hace mandando el array entero, no hay sub-recurso propio).
   * @param {string} portalId
   * @param {string} categoryId
   * @param {{ name?: string, type?: 'select'|'string'|'number'|'date'|'boolean', active?: boolean, required?: boolean, subcategories?: Array<{id?: string, name: string, active?: boolean}> }} patch
   */
  async updateTypificationCategory(portalId, categoryId, patch) {
    const response = await this._nthwFetch(`/typification/${portalId}/${categoryId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(patch),
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.message || `Error al actualizar la categoría (${response.status})`);
    }
    return await response.json();
  }

  /**
   * Elimina una categoría de tipificación.
   * @param {string} portalId
   * @param {string} categoryId
   */
  async deleteTypificationCategory(portalId, categoryId) {
    const response = await this._nthwFetch(`/typification/${portalId}/${categoryId}`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.message || `Error al eliminar la categoría (${response.status})`);
    }
    return await response.json().catch(() => ({}));
  }

  /**
   * Guarda la nota interna de la conversación (una sola nota, se sobreescribe).
   * No exige que la conversación esté reclamada por quien llama.
   * @param {string} conversationId
   * @param {string} note
   */
  async saveConversationNote(conversationId, note) {
    const response = await this._nthwFetch(
      `/conversations/${conversationId}/note`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ note }),
      }
    );
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.message || `Error al guardar la nota (${response.status})`);
    }
    return await response.json();
  }

  /**
   * Guarda (o borra) el valor de UNA categoría de tipificación de la
   * conversación mientras sigue abierta — las demás categorías no se tocan. No
   * exige que esté cerrada ni reclamada por quien llama.
   * @param {string} conversationId
   * @param {string} categoryId
   * @param {string|null} subcategoryId valor elegido; null borra la categoría
   */
  async saveConversationTypification(conversationId, categoryId, subcategoryId) {
    const response = await this._nthwFetch(
      `/conversations/${conversationId}/typification`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ categoryId, subcategoryId }),
      }
    );
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.message || `Error al guardar la tipificación (${response.status})`);
    }
    return await response.json();
  }

  /**
   * Envía un mensaje como el asesor humano (E3), de texto o con adjunto, o
   * una nota interna (E20) que nunca llega a WhatsApp.
   * Exactamente uno de `text`/`media` debe venir informado — lo exige el backend.
   * @param {string} conversationId
   * @param {{assignedTo: string, text?: string, media?: {url: string, kind: 'IMAGE'|'VIDEO'|'AUDIO'|'DOCUMENT', filename?: string, caption?: string}, type?: 'MESSAGE'|'NOTE'}} payload
   */
  async sendMessage(conversationId, { assignedTo, text, media, type }) {
    const response = await this._nthwFetch(
      `/conversations/${conversationId}/messages`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          assignedTo,
          ...(type === 'NOTE' ? { type, text } : media ? { media } : { text }),
        }),
      }
    );
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.message || `Error al enviar el mensaje (${response.status})`);
    }
    return await response.json();
  }

  /**
   * Pide una URL prefirmada de S3 para subir un adjunto de la bandeja nativa
   * (E7). El navegador hace el PUT directo a S3 — nunca pasa por Lambda.
   * @param {string} conversationId
   * @param {File} file
   * @returns {Promise<{uploadUrl: string, publicUrl: string, key: string, kind: string, maxBytes: number}>}
   */
  async getAttachmentUploadUrl(conversationId, file) {
    const response = await this._nthwFetch(
      `/conversations/${conversationId}/attachments/upload-url`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          filename: file.name || 'upload',
          contentType: file.type || 'application/octet-stream',
        }),
      }
    );
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(attachmentUploadErrorMessage(err, response.status, file));
    }
    return await response.json();
  }

  /**
   * Sube un adjunto de la bandeja nativa a S3 y devuelve la URL pública +
   * el `kind` que hay que mandar en `sendMessage`.
   * @param {string} conversationId
   * @param {File} file
   * @returns {Promise<{publicUrl: string, kind: string}>}
   */
  async uploadInboxAttachmentToS3(conversationId, file, { onProgress } = {}) {
    const { uploadUrl, publicUrl, kind, maxBytes, uploadContentType } =
      await this.getAttachmentUploadUrl(conversationId, file);
    if (typeof maxBytes === 'number' && file.size > maxBytes) {
      throw new Error(attachmentTooLargeMessage(file, kind, maxBytes));
    }
    // La URL viene firmada para un Content-Type exacto: el que devuelve el
    // servidor, que puede diferir del del archivo (un .md o .json se sube
    // como text/plain porque WhatsApp solo entrega como documento lo que
    // reconoce). Sin `uploadContentType` (servidor viejo) se usa el del archivo.
    const contentType = uploadContentType || file.type || 'application/octet-stream';
    if (typeof onProgress === 'function') {
      await putWithProgress(uploadUrl, file, contentType, onProgress);
      return { publicUrl, kind };
    }
    const putResponse = await fetch(uploadUrl, {
      method: 'PUT',
      headers: { 'Content-Type': contentType },
      body: file,
    });
    if (!putResponse.ok) {
      const err = await putResponse.text().catch(() => '');
      throw new Error(`Error al subir el adjunto (${putResponse.status}): ${err}`);
    }
    return { publicUrl, kind };
  }

  async getInteractions(conversationId, { viewerId } = {}) {
    const qs = this._buildQuery({ limit: 5000, viewerId });
    const response = await this._nthwFetch(
      `/conversations/${conversationId}/interactions${qs ? `?${qs}` : ''}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
      }
    );
    return await response.json();
  }

  /**
   * Lista paginada de tickets cerrados de la bandeja nativa (tabla
   * whatsapp-closed-tickets), del mas reciente al mas antiguo. Solo cubre los
   * cerrados desde que se desplego esa tabla; los anteriores siguen en
   * getClosedChats (Historial).
   * @param {string} hubSpotId
   * @param {{from?: string, to?: string, agent?: string, typificationCategoryId?: string,
   *   systemNumber?: string, q?: string, limit?: number, nextToken?: string,
   *   viewerId?: string, viewAll?: boolean}} [filters]
   * @returns {Promise<{items: object[], count: number, nextToken?: string, partial: boolean}>}
   */
  async getClosedTickets(hubSpotId, filters = {}) {
    const { viewAll, ...rest } = filters;
    const qs = this._buildQuery({ ...rest, viewAll: viewAll ? 'true' : undefined });
    const response = await this._closedTicketsFetch(
      `/${hubSpotId}/closed-tickets${qs ? `?${qs}` : ''}`,
      {
        method: 'GET',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      }
    );
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.message || `Error al cargar los tickets cerrados (${response.status})`);
    }
    return await response.json();
  }

  /**
   * Reportería de la bandeja nativa: tiempos, volumen, SLA y tipificaciones
   * sobre los tickets cerrados de un rango. Vive en el mismo RestApi aparte que
   * getClosedTickets (ver la nota ahí sobre el basePath "{env}-closed-tickets").
   * @param {string} hubSpotId
   * @param {{from?: string, to?: string, systemNumber?: string, agent?: string,
   *   tz?: string, claimTarget?: number, responseTarget?: number}} filters
   *   from/to: instantes ISO (el día local del usuario, no el día UTC);
   *   claimTarget/responseTarget: metas de SLA en segundos.
   */
  async getInboxReport(hubSpotId, filters = {}) {
    const qs = this._buildQuery(filters);
    const response = await this._closedTicketsFetch(
      `/${hubSpotId}/inbox-report${qs ? `?${qs}` : ''}`,
      { method: 'GET', headers: { 'Content-Type': 'application/json', Accept: 'application/json' } }
    );
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.message || `Error al cargar la reportería (${response.status})`);
    }
    return await response.json();
  }

  /** Cola en vivo de la bandeja: esperando sin asignar, la más antigua y carga por asesor. */
  async getInboxReportLive(hubSpotId, { systemNumber } = {}) {
    const qs = this._buildQuery({ systemNumber });
    const response = await this._closedTicketsFetch(
      `/${hubSpotId}/inbox-report/live${qs ? `?${qs}` : ''}`,
      { method: 'GET', headers: { 'Content-Type': 'application/json', Accept: 'application/json' } }
    );
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.message || `Error al cargar la cola en vivo (${response.status})`);
    }
    return await response.json();
  }

  /**
   * Reportería del widget web: embudo, atención por destino (Bandeja de Nua / HubSpot),
   * volumen, dominios y adjuntos. Vive en el mismo RestApi aparte que getInboxReport.
   * @param {string} hubSpotId
   * @param {{from?: string, to?: string, channelId?: string, domain?: string, tz?: string,
   *   claimTarget?: number, responseTarget?: number}} filters
   */
  async getWebchatReport(hubSpotId, filters = {}) {
    const qs = this._buildQuery(filters);
    const response = await this._closedTicketsFetch(
      `/${hubSpotId}/webchat-report${qs ? `?${qs}` : ''}`,
      { method: 'GET', headers: { 'Content-Type': 'application/json', Accept: 'application/json' } }
    );
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.message || `Error al cargar la reportería del widget (${response.status})`);
    }
    return await response.json();
  }

  /** Estado actual de las conversaciones del widget por destino: en el bot, esperando asesor, en atención. */
  async getWebchatReportLive(hubSpotId, { channelId } = {}) {
    const qs = this._buildQuery({ channelId });
    const response = await this._closedTicketsFetch(
      `/${hubSpotId}/webchat-report/live${qs ? `?${qs}` : ''}`,
      { method: 'GET', headers: { 'Content-Type': 'application/json', Accept: 'application/json' } }
    );
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.message || `Error al cargar el estado en vivo del widget (${response.status})`);
    }
    return await response.json();
  }

  async getClosedChats(hubSpotId, { limit = 5000, nextToken } = {}) {
    const qs = this._buildQuery({
      limit,
      nextToken,
    });
    const url = `${this.endpoint}/conversations/portal/${hubSpotId}/closed${
      qs ? `?${qs}` : ''
    }`;
    const response = await this._nthwFetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
    });
    return await response.json();
  }

  async getMetricsConversationsStarted({
    portalId,
    systemNumber,
    from,
    to,
    period = 300,
  }) {
    const formattedFrom = from ? dayjs.unix(from).toISOString() : undefined;
    const formattedTo = to ? dayjs.unix(to).toISOString() : undefined;
    const cleanedSystemNumber = systemNumber
      ? String(systemNumber).replace(/^\+/, '')
      : undefined;

    const qs = this._buildQuery({
      portalId,
      systemNumber: cleanedSystemNumber,
      from: formattedFrom,
      to: formattedTo,
      period,
    });
    const res = await this._nthwFetch(
      `/metrics/conversations/started?${qs}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
      }
    );
    if (!res.ok) throw new Error(await res.text());
    return await res.json();
  }

  async getMetricsInteractionsByActor({
    portalId,
    systemNumber,
    from,
    to,
    period = 300,
  }) {
    const formattedFrom = from ? dayjs.unix(from).toISOString() : undefined;
    const formattedTo = to ? dayjs.unix(to).toISOString() : undefined;
    const cleanedSystemNumber = systemNumber
      ? String(systemNumber).replace(/^\+/, '')
      : undefined;

    const qs = this._buildQuery({
      portalId,
      systemNumber: cleanedSystemNumber,
      from: formattedFrom,
      to: formattedTo,
      period,
    });
    const res = await this._nthwFetch(
      `/metrics/interactions/actor-id?${qs}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
      }
    );
    if (!res.ok) throw new Error(await res.text());
    return await res.json();
  }

  async getMetricsAutomationRate({
    portalId,
    systemNumber,
    from,
    to,
    period = 300,
  }) {
    const formattedFrom = from ? dayjs.unix(from).toISOString() : undefined;
    const formattedTo = to ? dayjs.unix(to).toISOString() : undefined;
    const cleanedSystemNumber = systemNumber
      ? String(systemNumber).replace(/^\+/, '')
      : undefined;

    const qs = this._buildQuery({
      portalId,
      systemNumber: cleanedSystemNumber,
      from: formattedFrom,
      to: formattedTo,
      period,
    });
    const res = await this._nthwFetch(`/metrics/automation/rate?${qs}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
    });
    if (!res.ok) throw new Error(await res.text());
    return await res.json();
  }

  async getMetricsDerivationRate({
    portalId,
    systemNumber,
    from,
    to,
    period = 300,
  }) {
    const formattedFrom = from ? dayjs.unix(from).toISOString() : undefined;
    const formattedTo = to ? dayjs.unix(to).toISOString() : undefined;
    const cleanedSystemNumber = systemNumber
      ? String(systemNumber).replace(/^\+/, '')
      : undefined;

    const qs = this._buildQuery({
      portalId,
      systemNumber: cleanedSystemNumber,
      from: formattedFrom,
      to: formattedTo,
      period,
    });
    const res = await this._nthwFetch(`/metrics/derivation/rate?${qs}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
    });
    if (!res.ok) throw new Error(await res.text());
    return await res.json();
  }

  async getMetricsLatency({
    portalId,
    systemNumber,
    category = 'incoming',
    stat = 'p95',
    from,
    to,
    period = 300,
  }) {
    const formattedFrom = from ? dayjs.unix(from).toISOString() : undefined;
    const formattedTo = to ? dayjs.unix(to).toISOString() : undefined;
    const cleanedSystemNumber = systemNumber
      ? String(systemNumber).replace(/^\+/, '')
      : undefined;

    const qs = this._buildQuery({
      portalId,
      systemNumber: cleanedSystemNumber,
      category,
      stat,
      from: formattedFrom,
      to: formattedTo,
      period,
    });
    const res = await this._nthwFetch(`/metrics/latency?${qs}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
    });
    if (!res.ok) throw new Error(await res.text());
    return await res.json();
  }

  async getMetricsTemplatesSent({
    portalId,
    systemNumber,
    from,
    to,
    period = 300,
  }) {
    const formattedFrom = from ? dayjs.unix(from).toISOString() : undefined;
    const formattedTo = to ? dayjs.unix(to).toISOString() : undefined;
    const cleanedSystemNumber = systemNumber
      ? String(systemNumber).replace(/^\+/, '')
      : undefined;

    const qs = this._buildQuery({
      portalId,
      systemNumber: cleanedSystemNumber,
      from: formattedFrom,
      to: formattedTo,
      period,
    });
    const res = await this._nthwFetch(`/metrics/templates/sent?${qs}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
    });
    if (!res.ok) throw new Error(await res.text());
    return await res.json();
  }

  // ─────────────────────────────────────────────────────────────────────────
  // METRICS V2 — reportería de plantillas agregada desde DynamoDB Streams
  // GET /metrics/v2/templates/summary                       → todas las plantillas
  // GET /metrics/v2/templates/{templateId}/series            → serie temporal de 1 plantilla
  // GET /metrics/v2/templates/{templateId}/heatmap            → grilla 7x24 de 1 plantilla
  // GET /metrics/v2/overview                                  → funnel + serie (+ heatmap) del portal
  // Público, sin auth. from/to en formato YYYY-MM-DD (inclusive).
  // `systemNumber` es opcional en todos: si se omite, agrega todo el portal;
  // si se envía, filtra a un solo número de WhatsApp (dígitos sin '+').
  // meta.* (conciliación con Meta) es siempre a nivel WABA/portal — al filtrar
  // por systemNumber puede venir en 0 o no reflejar el filtro.
  // ─────────────────────────────────────────────────────────────────────────

  async getMetricsV2TemplatesSummary({ portalId, from, to, systemNumber }) {
    const qs = this._buildQuery({ portalId, from, to, systemNumber });
    const res = await this._nthwFetch(
      `/metrics/v2/templates/summary?${qs}`,
      {
        method: 'GET',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      }
    );
    if (!res.ok) {
      const err = await res.text().catch(() => '');
      throw new Error(
        `Error al obtener el resumen de plantillas (${res.status}): ${err}`
      );
    }
    return await res.json();
  }

  async getMetricsV2TemplateSeries({
    portalId,
    templateId,
    from,
    to,
    granularity = 'day',
    systemNumber,
  }) {
    const qs = this._buildQuery({ portalId, from, to, granularity, systemNumber });
    const res = await this._nthwFetch(
      `/metrics/v2/templates/${encodeURIComponent(templateId)}/series?${qs}`,
      {
        method: 'GET',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      }
    );
    if (!res.ok) {
      const err = await res.text().catch(() => '');
      throw new Error(
        `Error al obtener la serie de la plantilla (${res.status}): ${err}`
      );
    }
    return await res.json();
  }

  async getMetricsV2TemplateHeatmap({ portalId, templateId, from, to, systemNumber }) {
    const qs = this._buildQuery({ portalId, from, to, systemNumber });
    const res = await this._nthwFetch(
      `/metrics/v2/templates/${encodeURIComponent(templateId)}/heatmap?${qs}`,
      {
        method: 'GET',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      }
    );
    if (!res.ok) {
      const err = await res.text().catch(() => '');
      throw new Error(
        `Error al obtener el heatmap de la plantilla (${res.status}): ${err}`
      );
    }
    return await res.json();
  }

  async getMetricsV2Overview({ portalId, from, to, granularity = 'day', systemNumber }) {
    const qs = this._buildQuery({ portalId, from, to, granularity, systemNumber });
    const res = await this._nthwFetch(`/metrics/v2/overview?${qs}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    });
    if (!res.ok) {
      const err = await res.text().catch(() => '');
      throw new Error(
        `Error al obtener la visión general de métricas (${res.status}): ${err}`
      );
    }
    return await res.json();
  }

  async getMetricsV2ConversationsSummary({ portalId, from, to, systemNumber }) {
    const qs = this._buildQuery({ portalId, from, to, systemNumber });
    const res = await this._nthwFetch(
      `/metrics/v2/conversations/summary?${qs}`,
      {
        method: 'GET',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      }
    );
    if (!res.ok) {
      const err = await res.text().catch(() => '');
      throw new Error(
        `Error al obtener el resumen de origen de conversaciones (${res.status}): ${err}`
      );
    }
    return await res.json();
  }

  async getMetricsV2ConversationsSeries({ portalId, from, to, granularity = 'day', systemNumber }) {
    const qs = this._buildQuery({ portalId, from, to, granularity, systemNumber });
    const res = await this._nthwFetch(
      `/metrics/v2/conversations/series?${qs}`,
      {
        method: 'GET',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      }
    );
    if (!res.ok) {
      const err = await res.text().catch(() => '');
      throw new Error(
        `Error al obtener la serie de origen de conversaciones (${res.status}): ${err}`
      );
    }
    return await res.json();
  }

  async getMetricsV2ServiceSummary({ portalId, from, to, systemNumber }) {
    const qs = this._buildQuery({ portalId, from, to, systemNumber });
    const res = await this._nthwFetch(
      `/metrics/v2/service/summary?${qs}`,
      {
        method: 'GET',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      }
    );
    if (!res.ok) {
      const err = await res.text().catch(() => '');
      throw new Error(
        `Error al obtener el resumen de mensajes de Servicio (${res.status}): ${err}`
      );
    }
    return await res.json();
  }

  async getMetricsV2ServiceSeries({ portalId, from, to, granularity = 'day', systemNumber }) {
    const qs = this._buildQuery({ portalId, from, to, granularity, systemNumber });
    const res = await this._nthwFetch(
      `/metrics/v2/service/series?${qs}`,
      {
        method: 'GET',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      }
    );
    if (!res.ok) {
      const err = await res.text().catch(() => '');
      throw new Error(
        `Error al obtener la serie de mensajes de Servicio (${res.status}): ${err}`
      );
    }
    return await res.json();
  }

  async getMetricsV2MessagesSummary({ portalId, from, to, systemNumber }) {
    const qs = this._buildQuery({ portalId, from, to, systemNumber });
    const res = await this._nthwFetch(
      `/metrics/v2/messages/summary?${qs}`,
      {
        method: 'GET',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      }
    );
    if (!res.ok) {
      const err = await res.text().catch(() => '');
      throw new Error(
        `Error al obtener el resumen de mensajes (${res.status}): ${err}`
      );
    }
    return await res.json();
  }

  async getMetricsV2MessagesSeries({ portalId, from, to, systemNumber }) {
    const qs = this._buildQuery({ portalId, from, to, systemNumber });
    const res = await this._nthwFetch(
      `/metrics/v2/messages/series?${qs}`,
      {
        method: 'GET',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      }
    );
    if (!res.ok) {
      const err = await res.text().catch(() => '');
      throw new Error(
        `Error al obtener la serie de mensajes (${res.status}): ${err}`
      );
    }
    return await res.json();
  }

  // GET /metrics/v2/ads/summary → conversaciones iniciadas desde anuncios de
  // Click to WhatsApp (Meta Ads). Catálogo a nivel de portal, no se filtra
  // por número de WhatsApp (un mismo anuncio puede abrir chats en cualquiera
  // de las líneas del portal).
  async getMetricsV2AdsSummary({ portalId, from, to }) {
    const qs = this._buildQuery({ portalId, from, to });
    const res = await this._nthwFetch(
      `/metrics/v2/ads/summary?${qs}`,
      {
        method: 'GET',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      }
    );
    if (!res.ok) {
      const err = await res.text().catch(() => '');
      throw new Error(
        `Error al obtener las conversaciones por anuncio (${res.status}): ${err}`
      );
    }
    return await res.json();
  }

  async getChannels() {
    const response = await this._nthwFetch(
      `/channels/${this.portalId}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
      }
    );
    if (!response.ok) {
      throw new Error(`Error al obtener los canales (${response.status})`);
    }
    return await response.json();
  }

  async getChannelTemplateCount(systemNumber) {
    const response = await this._nthwFetch(
      `/channels/${this.portalId}/systemNumber/${systemNumber}/templates/count`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
      }
    );
    if (!response.ok) {
      throw new Error(`Error al obtener conteo de templates (${response.status})`);
    }
    return await response.json();
  }

  async getChannelTemplateCounts() {
    const response = await this._nthwFetch(
      `/channels/${this.portalId}/template-counts`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
      }
    );
    if (!response.ok) {
      throw new Error(`Error al obtener conteos de templates (${response.status})`);
    }
    return await response.json();
  }

  async getChannelTemplates(systemNumber, { limit = 25, after, before } = {}) {
    const qs = this._buildQuery({ limit, after, before });
    const url = `${this.endpoint}/channels/${this.portalId}/systemNumber/${systemNumber}/templates${qs ? `?${qs}` : ''}`;
    const response = await this._nthwFetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
    });
    if (!response.ok) {
      throw new Error(`Error al obtener los templates del canal (${response.status})`);
    }
    return await response.json();
  }

  async _bulkRequest(path, init = {}) {
    if (!isCdkProxyConfigured()) {
      throw new Error(
        'Los envíos masivos no están disponibles porque el proxy de la aplicación no está configurado.'
      );
    }

    const token = await getIdToken();
    if (!token) {
      const error = new Error('No hay sesión activa');
      error.status = 401;
      throw error;
    }

    const response = await fetch(`${nuaProxyUrl()}${path}`, {
      ...init,
      method: init.method || 'GET',
      headers: {
        ...(init.headers || {}),
        Authorization: `Bearer ${token}`,
      },
    });
    const body = await response.json().catch(() => ({}));
    if (!response.ok) {
      const error = new Error(
        body?.message || 'No se pudo completar la solicitud. Intenta de nuevo.'
      );
      error.status = response.status;
      error.code = body?.error;
      error.payload = body;
      throw error;
    }
    return body;
  }

  async getBulkSends({ limit = 20, nextToken, signal } = {}) {
    const query = this._buildQuery({ limit, nextToken });
    return this._bulkRequest(
      `/bulk-sends/${encodeURIComponent(this.portalId)}${query ? `?${query}` : ''}`,
      { method: 'GET', signal }
    );
  }

  async getBulkSend(batchId, { limit = 50, nextToken, status, signal } = {}) {
    const query = this._buildQuery({ limit, nextToken, status });
    return this._bulkRequest(
      `/bulk-sends/${encodeURIComponent(this.portalId)}/${encodeURIComponent(batchId)}${
        query ? `?${query}` : ''
      }`,
      { method: 'GET', signal }
    );
  }

  async getBulkUploadUrl(fileName) {
    return this._bulkRequest(
      `/bulk-sends/${encodeURIComponent(this.portalId)}/upload-url`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ fileName }),
      }
    );
  }

  async uploadBulkFile(file, upload) {
    const response = await fetch(upload.uploadUrl, {
      method: upload.method || 'PUT',
      headers: { 'Content-Type': upload.contentType || file.type || 'application/octet-stream' },
      body: file,
    });
    if (!response.ok) {
      const error = new Error('No se pudo subir el archivo. Intenta de nuevo.');
      error.status = response.status;
      throw error;
    }
  }

  async parseBulkFile(payload) {
    return this._bulkRequest(
      `/bulk-sends/${encodeURIComponent(this.portalId)}/parse`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload),
      }
    );
  }

  async createBulkSend(payload) {
    return this._bulkRequest(`/bulk-sends/${encodeURIComponent(this.portalId)}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload),
    });
  }

  async searchBulkContacts(search, { limit = 10, signal, systemNumber } = {}) {
    const query = this._buildQuery({ search, limit, systemNumber });
    return this._bulkRequest(
      `/bulk-sends/${encodeURIComponent(this.portalId)}/contacts?${query}`,
      { method: 'GET', signal }
    );
  }

  /**
   * E21 — Envía una plantilla aprobada de Meta a la conversación ya abierta
   * en la bandeja nativa, con las variables del BODY llenadas a mano por el
   * asesor (sin autocompletar desde HubSpot, a diferencia de las campañas).
   * Mismo endpoint que usa el widget legacy de HubSpot (`POST /templates`),
   * con el payload de Meta ya armado del lado del cliente. `credentials` se
   * omite a propósito: el backend lo resuelve vía NTSR por portalId+systemNumber.
   * @param {{ channel: object, template: object, to: string, objectId: string, bodyValues: string[] }} params
   */
  async sendTemplateToConversation({ channel, template, to, objectId, bodyValues }) {
    const bodyComponent = (template.components || []).find((c) => c.type === 'BODY');
    const templateText = bodyValues.reduce(
      (text, value, index) => text.replaceAll(`{{${index + 1}}}`, value),
      bodyComponent?.text || ''
    );
    const components =
      bodyValues.length > 0
        ? [{ type: 'body', parameters: bodyValues.map((value) => ({ type: 'text', text: value })) }]
        : [];

    const payload = {
      messaging_product: 'whatsapp',
      to,
      type: 'template',
      template: {
        name: template.name,
        language: { code: template.language },
        components,
      },
      wabaId: channel.metaWabaId,
      channelId: channel.hubspotChannelId,
      portalId: this.portalId,
    };

    const response = await this._nthwFetch(`/templates`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        payload,
        portalId: this.portalId,
        phoneToUse: to,
        channelId: channel.hubspotChannelId,
        language: template.language,
        wabaId: channel.metaWabaId,
        metaPhoneNumberId: channel.metaPhoneNumberId,
        systemNumber: channel.systemNumber,
        hubspotChannelId: channel.hubspotChannelId,
        hubspotChannelAccountId: channel.hubspotChannelAccountId,
        category: template.category,
        templateId: template.id,
        templateText,
        disableVirtualAgent: false,
        objectId,
        objectType: 'CONTACT',
      }),
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(templateSendErrorMessage(err, response.status));
    }
    return await response.json();
  }

  /**
   * Obtiene la configuración de visibilidad de plantillas para un número.
   * Contrato: GET /channels/{portalId}/systemNumber/{systemNumber}/template-visibility
   * Respuesta: { enabledTemplateNames: string[] } — vacío/ausente = todas visibles.
   *
   * @param {string} systemNumber - Número de sistema (sin +)
   * @returns {Promise<{ enabledTemplateNames: string[] }>}
   */
  async getTemplateVisibility(systemNumber) {
    const response = await this._nthwFetch(
      `/channels/${this.portalId}/systemNumber/${systemNumber}/template-visibility`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
      }
    );
    if (!response.ok) {
      const err = await response.text().catch(() => '');
      throw new Error(
        `Error al obtener visibilidad de plantillas (${response.status}): ${err}`
      );
    }
    return await response.json();
  }

  /**
   * Guarda la configuración de visibilidad de plantillas para un número.
   * Contrato: PUT /channels/{portalId}/systemNumber/{systemNumber}/template-visibility
   * Body: { enabledTemplateNames: string[] } — array vacío = todas visibles.
   *
   * @param {string} systemNumber - Número de sistema (sin +)
   * @param {string[]} enabledNames - Nombres de plantillas habilitadas
   * @returns {Promise<any>}
   */
  async setTemplateVisibility(systemNumber, enabledNames) {
    const response = await this._nthwFetch(
      `/channels/${this.portalId}/systemNumber/${systemNumber}/template-visibility`,
      {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({ enabledTemplateNames: enabledNames }),
      }
    );
    if (!response.ok) {
      const err = await response.text().catch(() => '');
      throw new Error(
        `Error al guardar visibilidad de plantillas (${response.status}): ${err}`
      );
    }
    return await response.json().catch(() => ({}));
  }

  // ─────────────────────────────────────────────────────────────────────────
  // CATALOG — contratos F2
  // GET  /catalog/{portalId}                     → lista de catálogos
  // POST /catalog/{portalId}                     → crear catálogo
  // GET  /catalog/{portalId}/{catalogId}/products
  // POST /catalog/{portalId}/{catalogId}/products
  // PUT  /catalog/{portalId}/{catalogId}/products/{productId}
  // DELETE /catalog/{portalId}/{catalogId}/products/{productId}
  // POST /catalog/{portalId}/{catalogId}/upload-image → sube imagen, devuelve url
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Lista los catálogos disponibles para el portal.
   * @returns {Promise<{ data: Array }>}
   */
  async getCatalogs() {
    const response = await this._nthwFetch(
      `/catalog/${this.portalId}`,
      {
        method: 'GET',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      }
    );
    if (!response.ok) {
      const err = await response.text().catch(() => '');
      throw new Error(`Error al obtener catálogos (${response.status}): ${err}`);
    }
    return await response.json();
  }

  /**
   * Crea un nuevo catálogo en el portal.
   * @param {{ name: string, systemNumber: string }} data
   * @returns {Promise<any>}
   */
  async createCatalog(data) {
    const response = await this._nthwFetch(
      `/catalog/${this.portalId}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(data),
      }
    );
    if (!response.ok) {
      const err = await response.text().catch(() => '');
      throw new Error(`Error al crear el catálogo (${response.status}): ${err}`);
    }
    return await response.json();
  }

  /**
   * Lista los productos de un catálogo.
   * @param {string} catalogId
   * @param {{ limit?: number, after?: string }} [opts]
   * @returns {Promise<{ data: Array, paging?: object }>}
   */
  async getProducts(catalogId, { limit = 25, after } = {}) {
    const qs = this._buildQuery({ limit, after });
    const url = `${this.endpoint}/catalog/${this.portalId}/${catalogId}/products${qs ? `?${qs}` : ''}`;
    const response = await this._nthwFetch(url, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    });
    if (!response.ok) {
      const err = await response.text().catch(() => '');
      throw new Error(`Error al obtener productos (${response.status}): ${err}`);
    }
    return await response.json();
  }

  /**
   * Crea un producto en un catálogo.
   * @param {string} catalogId
   * @param {object} productData - { retailer_id, name, description, price, currency, image_url, ... }
   * @returns {Promise<any>}
   */
  async createProduct(catalogId, productData) {
    const response = await this._nthwFetch(
      `/catalog/${this.portalId}/${catalogId}/products`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(productData),
      }
    );
    if (!response.ok) {
      const err = await response.text().catch(() => '');
      throw new Error(`Error al crear el producto (${response.status}): ${err}`);
    }
    return await response.json();
  }

  /**
   * Actualiza un producto existente en un catálogo.
   * @param {string} catalogId
   * @param {string} productId  - retailer_id del producto
   * @param {object} productData
   * @returns {Promise<any>}
   */
  async updateProduct(catalogId, productId, productData) {
    const response = await this._nthwFetch(
      `/catalog/${this.portalId}/${catalogId}/products/${encodeURIComponent(productId)}`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(productData),
      }
    );
    if (!response.ok) {
      const err = await response.text().catch(() => '');
      throw new Error(`Error al actualizar el producto (${response.status}): ${err}`);
    }
    return await response.json();
  }

  /**
   * Elimina un producto de un catálogo.
   * @param {string} catalogId
   * @param {string} productId  - retailer_id del producto
   * @returns {Promise<any>}
   */
  async deleteProduct(catalogId, productId) {
    const response = await this._nthwFetch(
      `/catalog/${this.portalId}/${catalogId}/products/${encodeURIComponent(productId)}`,
      {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      }
    );
    if (!response.ok) {
      const err = await response.text().catch(() => '');
      throw new Error(`Error al eliminar el producto (${response.status}): ${err}`);
    }
    return await response.json().catch(() => ({}));
  }

  /**
   * Sube la imagen de un producto al catálogo.
   * El backend espera `multipart/form-data` con el campo `file`.
   * Devuelve `{ image_url: string }`.
   *
   * @param {string} catalogId
   * @param {File} file - Objeto File del input de imagen
   * @returns {Promise<{ image_url: string }>}
   */
  async uploadProductImage(catalogId, file) {
    const formData = new FormData();
    formData.append('file', file);
    const response = await this._nthwFetch(
      `/catalog/${this.portalId}/${catalogId}/upload-image`,
      {
        method: 'POST',
        // No establecer Content-Type manualmente — el browser lo agrega con el boundary correcto
        body: formData,
      }
    );
    if (!response.ok) {
      const err = await response.text().catch(() => '');
      throw new Error(`Error al subir la imagen (${response.status}): ${err}`);
    }
    return await response.json();
  }

  // ─────────────────────────────────────────────────────────────────────────
  // MEDIA UPLOAD — F2: subida de media a Meta vía backend (Resumable Upload API)
  // POST /catalog/{portalId}/upload-media
  // Body: FormData con `file` + `systemNumber`
  // Responde: { header_handle: string }
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Sube un archivo de media (imagen/video/documento) al backend que lo
   * procesa vía la Meta Resumable Upload API y devuelve el `header_handle`.
   *
   * @param {File} file - Archivo a subir
   * @param {string} systemNumber - Número del canal (scoped credentials)
   * @returns {Promise<{ header_handle: string }>}
   */
  async uploadTemplateMedia(file, systemNumber) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('systemNumber', systemNumber);
    const response = await this._nthwFetch(
      `/catalog/${this.portalId}/upload-media`,
      {
        method: 'POST',
        body: formData,
      }
    );
    if (!response.ok) {
      const err = await response.text().catch(() => '');
      throw new Error(`Error al subir la media (${response.status}): ${err}`);
    }
    return await response.json();
  }

  // ─────────────────────────────────────────────────────────────────────────
  // SUBIDA DIRECTA A S3 — reemplaza a uploadTemplateMedia para archivos grandes
  // POST /catalog/{portalId}/media-upload-url  →  { uploadUrl, publicUrl }
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Pide al backend una URL prefirmada para subir media directamente a S3.
   *
   * El archivo no viaja por la API: `uploadTemplateMedia` lo mandaba como
   * multipart y ahí topaba en ~4.5 MB, porque API Gateway codifica el cuerpo en
   * base64 y la invocación de Lambda no admite más de 6 MB de payload. Subiendo
   * directo a S3 el límite pasa a ser el de WhatsApp (16 MB para video).
   *
   * @param {File} file - Archivo cuyo nombre y tipo se usarán para firmar
   * @param {string} systemNumber - Número del canal
   * @returns {Promise<{ uploadUrl: string, publicUrl: string, key: string }>}
   */
  async getMediaUploadUrl(file, systemNumber) {
    const response = await this._nthwFetch(
      `/catalog/${this.portalId}/media-upload-url`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          filename: file.name || 'upload',
          contentType: file.type || 'application/octet-stream',
          systemNumber,
        }),
      }
    );
    if (!response.ok) {
      const err = await response.text().catch(() => '');
      throw new Error(`Error al preparar la subida (${response.status}): ${err}`);
    }
    return await response.json();
  }

  /**
   * Sube el archivo a S3 con la URL prefirmada y devuelve su URL pública.
   *
   * El PUT va directo a S3: es un fetch corriente, sin SDK de AWS ni
   * credenciales, porque la firma viaja dentro de la propia URL. El
   * `Content-Type` debe coincidir con el que se usó para firmar o S3 rechaza
   * la petición.
   *
   * @param {File} file - Archivo a subir (ya validado y corregido)
   * @param {string} systemNumber - Número del canal
   * @returns {Promise<string>} URL pública del archivo en S3
   */
  async uploadTemplateMediaToS3(file, systemNumber) {
    const { uploadUrl, publicUrl } = await this.getMediaUploadUrl(file, systemNumber);

    const putResponse = await fetch(uploadUrl, {
      method: 'PUT',
      headers: { 'Content-Type': file.type || 'application/octet-stream' },
      body: file,
    });
    if (!putResponse.ok) {
      const err = await putResponse.text().catch(() => '');
      throw new Error(`Error al subir la media (${putResponse.status}): ${err}`);
    }
    return publicUrl;
  }

  async getMetricForTemplate({
    systemNumber,
    templateId,
    from,
    to,
    granularity = 'daily',
  }) {
    const cleanedSystemNumber = systemNumber
      ? String(systemNumber).replace(/^\+/, '')
      : undefined;

    const qs = this._buildQuery({
      systemNumber: cleanedSystemNumber,
      templateId,
      start: from,
      end: to,
      granularity,
    });
    const res = await this._nthwFetch(`${this.baseUrl}/template/${templateId}/insights?${qs}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
    });
    if (!res.ok) throw new Error(await res.text());
    return await res.json();
  }

  // ─────────────────────────────────────────────────────────────────────────
  // LIMITS — contratos F3
  //
  // Bolsas de límite por portal o por número:
  //   GET    /limits/{portalId}                           → lista bolsas del portal
  //   POST   /limits/{portalId}                           → crear bolsa
  //   PUT    /limits/{portalId}/{bagId}                   → actualizar bolsa
  //   DELETE /limits/{portalId}/{bagId}                   → eliminar bolsa
  //   GET    /limits/{portalId}/usage                     → consumo actual (todas las bolsas)
  //
  // Estructura de una bolsa (body POST/PUT):
  //   {
  //     metric: 'templates' | 'conversations' | 'outbound_messages',
  //     totalAmount: number,
  //     period: 'daily' | 'weekly' | 'monthly' | 'custom',
  //     periodDays?: number,            // solo cuando period === 'custom'
  //     scope: 'portal' | 'number',
  //     systemNumber?: string,          // requerido cuando scope === 'number'
  //     blockOnExceed: boolean,
  //     alertOnly: boolean,
  //     alertThresholds: number[],      // porcentajes, ej: [50, 80, 100]
  //   }
  //
  // Respuesta de /usage (array de items):
  //   {
  //     bagId: string,
  //     metric: string,
  //     totalAmount: number,
  //     usedAmount: number,
  //     scope: 'portal' | 'number',
  //     systemNumber?: string,
  //     blockOnExceed: boolean,
  //     alertOnly: boolean,
  //     alertThresholds: number[],
  //     period: string,
  //     currentPeriodStart: string,   // ISO-8601
  //   }
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Lista todas las bolsas de límite del portal.
   * @returns {Promise<{ data: Array }>}
   */
  async getLimitBags() {
    const response = await this._nthwFetch(
      `/limits/${this.portalId}`,
      {
        method: 'GET',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      }
    );
    if (!response.ok) {
      const err = await response.text().catch(() => '');
      throw new Error(`Error al obtener bolsas de límite (${response.status}): ${err}`);
    }
    return await response.json();
  }

  /**
   * Crea una nueva bolsa de límite.
   * @param {object} bagData
   * @returns {Promise<any>}
   */
  async createLimitBag(bagData) {
    const response = await this._nthwFetch(
      `/limits/${this.portalId}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(bagData),
      }
    );
    if (!response.ok) {
      const err = await response.text().catch(() => '');
      throw new Error(`Error al crear la bolsa de límite (${response.status}): ${err}`);
    }
    return await response.json();
  }

  /**
   * Actualiza una bolsa de límite existente.
   * @param {string} bagId
   * @param {object} bagData
   * @returns {Promise<any>}
   */
  async updateLimitBag(bagId, bagData) {
    const response = await this._nthwFetch(
      `/limits/${this.portalId}/${encodeURIComponent(bagId)}`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(bagData),
      }
    );
    if (!response.ok) {
      const err = await response.text().catch(() => '');
      throw new Error(`Error al actualizar la bolsa de límite (${response.status}): ${err}`);
    }
    return await response.json();
  }

  /**
   * Elimina una bolsa de límite.
   * @param {string} bagId
   * @returns {Promise<any>}
   */
  async deleteLimitBag(bagId) {
    const response = await this._nthwFetch(
      `/limits/${this.portalId}/${encodeURIComponent(bagId)}`,
      {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      }
    );
    if (!response.ok) {
      const err = await response.text().catch(() => '');
      throw new Error(`Error al eliminar la bolsa de límite (${response.status}): ${err}`);
    }
    return await response.json().catch(() => ({}));
  }

  /**
   * Obtiene el consumo actual de todas las bolsas del portal.
   * Incluye usedAmount, totalAmount, y metadatos de cada bolsa.
   * @returns {Promise<{ data: Array }>}
   */
  async getLimitsUsage() {
    const response = await this._nthwFetch(
      `/limits/${this.portalId}/usage`,
      {
        method: 'GET',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      }
    );
    if (!response.ok) {
      const err = await response.text().catch(() => '');
      throw new Error(`Error al obtener consumo de límites (${response.status}): ${err}`);
    }
    return await response.json();
  }

}

export default NuaTalkerNthwService;
